import React from 'react';
import classNames from 'classnames';

import { ChartTooltip, formatShare, useHoveredSegment } from '../chart-tooltip';

export interface BarSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export interface SegmentedBarProps {
  /** Left to right. Zero or negative values are skipped. */
  segments: BarSegment[];
  /** The value that fills the whole track. Pass the same number to every bar that should compare. */
  scaleMax: number;
  /**
   * What the hover percentage is a share of, e.g. the row's Allocated. Defaults to the sum of the segments. When it is
   * zero or less the tooltip names the segment without a percentage.
   */
  percentBase?: number;
  /** Shows the segment's label and percentage beside the pointer on hover. Default true. */
  showTooltip?: boolean;
  /** Accessible name (role="img"); when omitted the bar is aria-hidden (decorative). */
  ariaLabel?: string;
  className?: string;
  dataCy?: string;
}

/** A pure CSS stacked bar. Segment widths are a share of `scaleMax`, and the running total is clipped at 100%. */
const SegmentedBar: React.FC<SegmentedBarProps> = ({
  segments,
  scaleMax,
  percentBase,
  showTooltip = true,
  ariaLabel,
  className,
  dataCy = 'segmented-bar',
}) => {
  const { hovered, onMove, onLeave } = useHoveredSegment();
  const positive = segments.filter(({ value }) => value > 0);
  const base = percentBase ?? positive.reduce((sum, { value }) => sum + value, 0);

  let used = 0;
  const bars =
    scaleMax > 0
      ? positive.map(({ key, value, color }) => {
          const pct = Math.max(0, Math.min((value / scaleMax) * 100, 100 - used));
          used += pct;
          return (
            <span
              key={key}
              className={classNames('segmented-bar-segment', { hovered: hovered?.key === key })}
              data-key={key}
              style={{ width: `${pct}%`, '--segmented-bar-color': color } as React.CSSProperties}
              onMouseMove={showTooltip ? (event) => onMove(key, event) : undefined}
              onMouseLeave={showTooltip ? onLeave : undefined}
            />
          );
        })
      : [];

  const hoveredSegment = hovered && positive.find(({ key }) => key === hovered.key);

  return (
    <>
      <div
        className={classNames('segmented-bar', className)}
        role={ariaLabel ? 'img' : undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaLabel ? undefined : true}
        data-cy={dataCy}
      >
        {bars}
      </div>
      {hovered && hoveredSegment && (
        <ChartTooltip
          x={hovered.x}
          y={hovered.y}
          label={hoveredSegment.label}
          value={base > 0 ? formatShare(hoveredSegment.value, base) : undefined}
          color={hoveredSegment.color}
          dataCy={`${dataCy}-tooltip`}
        />
      )}
    </>
  );
};

export default SegmentedBar;
